import React from 'react';
import { useSilentAuthCheck } from 'modules/auth/ui/hooks/use-silent-auth-check';
import { useTrackEventOnce } from 'shared/analytics/use-track-event-once';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import usePlatform from 'shared/hooks/use-platform';
import MobileStub from 'ud-ui/components/stubs/mobile-stub';
import TabletStub from 'ud-ui/components/stubs/tablet-stub';

export interface MobileGuardProps {
  children: React.ReactNode;
}

/**
 * Рисует приложение только на ширине компьютера (`usePlatform`: от 900 px); на телефоне — `MobileStub`, на
 * планшете — `TabletStub`. Супер-админ обходит заглушку.
 *
 * На заглушке `SecureRoute` не монтируется, поэтому вход проверяется тихо (`useSilentAuthCheck`) — без перехода на
 * страницу входа, только чтобы Amplitude узнал пользователя. После проверки уходит `Mobile Stub Shown` (IMGM-1609).
 */
const MobileGuard: React.FC<MobileGuardProps> = ({ children }) => {
  const isAdmin = useIsAdmin();
  const { isMobile, isTablet, isDesktop, isLandscape, mobileOS } = usePlatform();

  const isStubShown = !isAdmin && !isDesktop;
  const isAuthSettled = useSilentAuthCheck(isStubShown);

  useTrackEventOnce(
    'Mobile Stub Shown',
    { stub: isTablet ? 'tablet' : 'mobile', mobile_os: mobileOS },
    // Ждём конца проверки: супер-админ узнаётся ею и уходит в приложение (`isStubShown` гаснет) — события нет.
    isStubShown && isAuthSettled,
  );

  if (isAdmin) {
    return <>{children}</>;
  }

  if (isMobile) {
    return <MobileStub isLandscape={isLandscape} />;
  }

  if (isTablet) {
    return <TabletStub isLandscape={isLandscape} />;
  }

  if (!isDesktop) {
    // Fallback (shouldn't happen, but keep previous behavior)
    return <MobileStub />;
  }

  return <>{children}</>;
};

export default MobileGuard;
